import { from } from 'rxjs'
import {
  catchError,
  filter,
  map,
  switchMap,
  withLatestFrom,
} from 'rxjs/operators'
import { RootEpic } from '~/Store/types'
import { errorNotification, LicenseService } from '~/Services'
import {
  OrganizationsService,
  OrgRoleItemResponse,
} from '~/Services/OrganizationService'
import { OrganizationActions } from './slice'
import { UserActions } from '../User'

const getLicenseInfoEpic: RootEpic = (action$, state$) =>
  action$.pipe(
    filter(OrganizationActions.getLicenseInfo.match),
    switchMap(action => {
      const { payload } = action
      return from(LicenseService.Get.getLicenseInfoById(payload)).pipe(
        map(res => OrganizationActions.getLicenseInfoSuccess(res.data)),
        catchError(error => {
          errorNotification(error)
          return [OrganizationActions.getLicenseInfoFail()]
        }),
      )
    }),
  )

const registerUserByAdminOrgEpic: RootEpic = action$ =>
  action$.pipe(
    filter(OrganizationActions.registerUserByAdminOrgRequest.match),
    switchMap(action => {
      const { payload } = action
      return from(
        OrganizationsService.Post.addUserToOrganization(payload),
      ).pipe(
        map(res => OrganizationActions.registerUserByAdminOrgSuccess(res.data)),
        catchError(error => {
          errorNotification(error)
          return [OrganizationActions.registerUserByAdminOrgFailed()]
        }),
      )
    }),
  )

const removeUsersFromOrganizationEpic: RootEpic = (action$, state$) =>
  action$.pipe(
    filter(OrganizationActions.removeUsersFromOrganization.match),
    switchMap(action => {
      const { orgId, userIds } = action.payload
      return from(OrganizationsService.Delete.removeUsers(orgId, userIds)).pipe(
        map(res =>
          OrganizationActions.removeUsersFromOrganizationSuscess(res.data),
        ),
        map(() => UserActions.getMyOrganizations()),
        catchError(error => {
          errorNotification(error)
          return [OrganizationActions.removeUsersFromOrganizationFail()]
        }),
      )
    }),
  )

const filterUsersToInviteEpic: RootEpic = (action$, state$) =>
  action$.pipe(
    filter(OrganizationActions.filterUsersToInvite.match),
    switchMap(action => {
      const { payload } = action
      return from(OrganizationsService.Get.getUsersToInvite(payload)).pipe(
        map(res => OrganizationActions.filterUsersToInviteSuccess(res.data)),
        catchError(error => {
          errorNotification(error)
          return [OrganizationActions.filterUsersToInviteSuccess]
        }),
      )
    }),
  )

const inviteUsersToOrgEpic: RootEpic = (action$, state$) =>
  action$.pipe(
    filter(OrganizationActions.inviteUsersToOrg.match),
    withLatestFrom(state$),
    switchMap(([action, state]) => {
      const { orgId, userIds, andNewUser } = action.payload
      return from(
        OrganizationsService.Post.addOrganizationUser(orgId, userIds),
      ).pipe(
        map(() => OrganizationActions.inviteUsersToOrgSuccess({ andNewUser })),
        catchError(error => {
          errorNotification(error)
          return [OrganizationActions.inviteUsersToOrgFail()]
        }),
      )
    }),
  )

const getOrganizationUsersEpic: RootEpic = (action$, state$) =>
  action$.pipe(
    filter(OrganizationActions.getOrganizationUsers.match),
    switchMap(action => {
      const { orgId, filterData, roles, invitationStatus } = action.payload
      return from(
        OrganizationsService.Get.getOrganizationUsers(
          orgId,
          filterData,
          roles,
          invitationStatus,
        ),
      ).pipe(
        map(res => OrganizationActions.getOrganizationUsersSuccess(res.data)),
        catchError(error => {
          errorNotification(error)
          return [OrganizationActions.getOrganizationUsersFail()]
        }),
      )
    }),
  )

// Get organization roles
const getOrganizationRolesEpic: RootEpic = (action$, state$) =>
  action$.pipe(
    filter(OrganizationActions.getOrganizationRoles.match),
    switchMap(action => {
      const orgId = action.payload
      return from(OrganizationsService.Get.getOrganizationRoles(orgId)).pipe(
        map(res =>
          OrganizationActions.getOrganizationRolesSuccess(
            (res as any).data as OrgRoleItemResponse[],
          ),
        ),
        catchError(error => {
          errorNotification(error)
          return [OrganizationActions.getOrganizationRolesFail()]
        }),
      )
    }),
  )

// Create organization role
const createOrganizationRoleEpic: RootEpic = (action$, state$) =>
  action$.pipe(
    filter(OrganizationActions.createOrganizationRole.match),
    switchMap(action => {
      const { orgId, roleNames } = action.payload
      return from(
        OrganizationsService.Post.createOrganizationRole(orgId, roleNames),
      ).pipe(
        map(res =>
          OrganizationActions.createOrganizationRoleSuccess(
            (res as any).data as OrgRoleItemResponse[],
          ),
        ),
        catchError(error => {
          errorNotification(error)
          return [OrganizationActions.createOrganizationRoleFail()]
        }),
      )
    }),
  )

// Update organization role
const updateOrganizationRoleEpic: RootEpic = (action$, state$) =>
  action$.pipe(
    filter(OrganizationActions.updateOrganizationRole.match),
    switchMap(action => {
      const { orgId, oldRoleName, newRoleName } = action.payload
      return from(
        OrganizationsService.Put.updateOrganizationRole(orgId, {
          roleName: oldRoleName,
          newRoleName: newRoleName,
        }),
      ).pipe(
        map(res => OrganizationActions.updateOrganizationRoleSuccess(res.data)),
        catchError(error => {
          errorNotification(error)
          return [OrganizationActions.updateOrganizationRoleFail()]
        }),
      )
    }),
  )

// Delete organization role
const deleteOrganizationRoleEpic: RootEpic = (action$, state$) =>
  action$.pipe(
    filter(OrganizationActions.deleteOrganizationRole.match),
    switchMap(action => {
      const { orgId, roleNames } = action.payload
      return from(
        OrganizationsService.Delete.removeRoleFromOrganization(
          orgId,
          roleNames,
        ),
      ).pipe(
        map(() => OrganizationActions.deleteOrganizationRoleSuccess(roleNames)),
        catchError(error => {
          errorNotification(error)
          return [OrganizationActions.deleteOrganizationRoleFail()]
        }),
      )
    }),
  )

export const OrganizationEpics = [
  getLicenseInfoEpic,
  filterUsersToInviteEpic,
  registerUserByAdminOrgEpic,
  removeUsersFromOrganizationEpic,
  inviteUsersToOrgEpic,
  getOrganizationUsersEpic,
  getOrganizationRolesEpic,
  createOrganizationRoleEpic,
  updateOrganizationRoleEpic,
  deleteOrganizationRoleEpic,
]
